import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faFileCode, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import moment from 'moment';
import BZAPIService from '../../services/BZAPIService';

const POLL_MS = 5000;

// How long an unfinished job is believed before we stop believing it.
//
// A job whose Step Functions execution was aborted never runs Cleanup, so its
// row keeps `finished = 0` / `status = RUNNING` forever. Without a bound it
// would be polled every POLL_MS for as long as the modal is open and shown as
// "In progress" indefinitely. Comfortably longer than a real render (the
// largest report the page budget permits is a few minutes), so a healthy job
// is never cut off early.
const STALE_AFTER_MINUTES = 30;

// The one phase whose per-version tally means anything to a reader. Everything
// else is a single step, so "3 of 8" would be noise (or wrong).
const COUNTED_PHASE = 'Analysing versions';

// A job is presumed abandoned once it has been unfinished for longer than the
// bound. Measured from the row's own createdAt rather than from when this
// component mounted, so a job stranded yesterday is never polled at all.
const isAbandoned = job => {
  if (job.finished) return false;
  const age = moment.utc().diff(moment.utc(job.createdAt), 'minutes');
  // An unparseable createdAt yields NaN; NaN > n is false, so the job keeps
  // its benefit of the doubt rather than being written off on bad data.
  return age > STALE_AFTER_MINUTES;
};

const statusBadge = job => {
  if (job.status === 'COMPLETED') return <span className="bz-pill bz-pill--ok">Completed</span>;
  if (job.status === 'FAILED') {
    // The reason is on the badge rather than its own column, so the row stays
    // clean without the information being unreachable.
    return (
      <span className="bz-pill bz-pill--bad" title={job.errorMessage || 'No reason recorded'}>
        Failed
      </span>
    );
  }
  if (isAbandoned(job)) {
    return (
      <span
        className="bz-pill bz-pill--warn"
        title={`This export has reported no result for over ${STALE_AFTER_MINUTES} minutes, so it has almost certainly stopped. Nothing further will be reported for it — start a new export. Refresh to check again.`}
      >
        Stopped responding
      </span>
    );
  }
  return <span className="bz-pill bz-pill--info">In progress</span>;
};

/**
 * Sub-line under an in-progress badge.
 *
 * `done` is deliberately omitted by get_export_progress.py when the execution
 * history page was truncated — reporting a number that could go BACKWARDS on
 * the next poll is worse than reporting none — so it arrives as null, and the
 * count is only rendered when it is actually a number. `phase` always has a
 * value: a job row that predates execution tracking reports "Preparing", which
 * is a live status, not an error.
 */
const progressLine = p => {
  if (!p || !p.phase) return null;
  const counted = p.phase === COUNTED_PHASE && Number.isFinite(p.done) && Number.isFinite(p.total) && p.total > 0;
  return (
    <div className="bz-muted" style={{ fontSize: 'var(--fs-xs)', marginTop: 'var(--sp-1)' }}>
      {p.phase}
      {counted ? ` — ${p.done} of ${p.total}` : ''}
    </div>
  );
};

const ExportHistory = ({ projectId }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({});
  // { jobId, format } for the one presign in flight, so the button the user
  // actually clicked is the one that spins.
  const [downloading, setDownloading] = useState(null);
  // Jobs we have stopped polling because they crossed STALE_AFTER_MINUTES.
  // Held in state, not a ref, because dropping one has to re-run the polling
  // effect so the interval is torn down once nothing live is left.
  const [abandonedIds, setAbandonedIds] = useState([]);
  const timer = useRef(null);
  // Jobs we have already re-listed for. Re-listing is what ends the polling,
  // so if a row ever failed to flip to `finished` the reload would retrigger
  // this effect and poll in a hot loop; one reload per job bounds that.
  const reloadedFor = useRef(new Set());

  const load = useCallback(async () => {
    try {
      setLoading(true);
      // Re-listing is the authoritative check, so the give-up list is dropped
      // and re-derived from the rows that come back — a job that has since
      // finished stops being written off. Guarded so an already-empty list
      // keeps its identity and does not needlessly restart the polling effect.
      setAbandonedIds(prev => (prev.length ? [] : prev));
      const res = await BZAPIService.bsaListExports({ bsaProjectId: projectId });
      setJobs(res?.valid && res?.jobs ? res.jobs : []);
    } catch (error) {
      console.error('Failed to load exports:', error);
      toast.error('Failed to load export history');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  // Poll progress only while something is unfinished, and stop as soon as
  // nothing is running — an idle tab (or one left open on screen) must make no
  // requests at all. The cleanup also covers unmount, which is the common case
  // here: the whole component goes away when the modal closes.
  useEffect(() => {
    const running = jobs.filter(j => !j.finished && !abandonedIds.includes(j.jobId));
    if (!running.length) return undefined;

    let cancelled = false;
    const tick = async () => {
      // Age is re-checked every tick, not just when `jobs` changes, so a job
      // that crosses the bound while the modal sits open is dropped promptly.
      // Recording it re-runs this effect with a smaller `running`, and clears
      // the interval entirely once nothing live is left.
      const expired = running.filter(isAbandoned).map(j => j.jobId);
      if (expired.length) {
        setAbandonedIds(prev => [...new Set([...prev, ...expired])]);
      }
      const live = running.filter(j => !expired.includes(j.jobId));
      if (!live.length) return;

      const next = {};
      let anyFinished = false;
      await Promise.all(
        live.map(async j => {
          try {
            const p = await BZAPIService.bsaExportProgress(j.jobId);
            if (p?.valid) next[j.jobId] = p;
            if (p?.finished && !reloadedFor.current.has(j.jobId)) {
              reloadedFor.current.add(j.jobId);
              anyFinished = true;
            }
          } catch (error) {
            // A failed poll is not worth a toast; the next tick retries.
          }
        })
      );
      if (cancelled) return;
      setProgress(prev => ({ ...prev, ...next }));
      // Re-listing is what flips `finished` on the row (and hands us the
      // download links), so it is the only thing that can end the polling.
      if (anyFinished) load();
    };

    tick();
    timer.current = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      if (timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }
    };
  }, [jobs, abandonedIds, load]);

  const download = async (jobId, format) => {
    try {
      setDownloading({ jobId, format });
      // The list endpoint only presigns URLs when asked for a single job.
      const res = await BZAPIService.bsaListExports({ jobId });
      if (!res?.valid || !res?.job) {
        toast.error('Failed to get download URL');
        return;
      }
      const url = format === 'pdf' ? res.job.pdfUrl : res.job.htmlUrl;
      if (!url) {
        toast.error(`${format.toUpperCase()} file is not available`);
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Failed to download:', error);
      toast.error(`Failed to download ${format.toUpperCase()}`);
    } finally {
      setDownloading(null);
    }
  };

  if (loading && !jobs.length) {
    return (
      <div className="bz-app" style={{ background: 'transparent', textAlign: 'center', padding: 'var(--sp-6)' }}>
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className="bz-app" style={{ background: 'transparent' }}>
      <div className="bz-row" style={{ justifyContent: 'space-between', marginBottom: 'var(--sp-4)' }}>
        <h3 className="bz-h3" style={{ margin: 0 }}>
          Export history
        </h3>
        <button type="button" className="bz-btn bz-btn--sm" onClick={load} disabled={loading}>
          <FontAwesomeIcon icon={faRefresh} className={loading ? 'fa-spin' : ''} />
          {' Refresh'}
        </button>
      </div>

      {!jobs.length ? (
        <div className="bz-card--flat bz-muted" style={{ padding: 'var(--sp-6)', textAlign: 'center' }}>
          No exports have been created for this project yet.
        </div>
      ) : (
        <div className="bz-card--flat bz-scroll" style={{ overflowX: 'auto' }}>
          <table className="bz-table">
            <thead>
              <tr>
                <th>Report</th>
                <th>Author</th>
                <th>Created</th>
                <th>Status</th>
                <th>Download</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(job => {
                const abandoned = isAbandoned(job);
                const rowBusy = downloading?.jobId === job.jobId;
                return (
                  <tr key={job.jobId}>
                    <td>{job.reportName}</td>
                    <td>{job.author}</td>
                    <td>{moment.utc(job.createdAt).format('YYYY-MM-DD HH:mm')}</td>
                    <td>
                      {statusBadge(job)}
                      {/* Named phase, not a percentage: "Building the report" is
                          most of the wall clock, so a bar sits at 100% looking hung. */}
                      {!job.finished && !abandoned && progressLine(progress[job.jobId])}
                      {abandoned && (
                        <div className="bz-muted" style={{ fontSize: 'var(--fs-xs)', marginTop: 'var(--sp-1)' }}>
                          No result after {STALE_AFTER_MINUTES} minutes — no longer checking. Start a new export.
                        </div>
                      )}
                    </td>
                    <td>
                      {job.finished && job.status !== 'FAILED' ? (
                        <div className="bz-row" style={{ gap: 'var(--sp-2)' }}>
                          <button
                            type="button"
                            className="bz-btn bz-btn--sm"
                            title="Download PDF"
                            disabled={rowBusy}
                            onClick={() => download(job.jobId, 'pdf')}
                          >
                            {rowBusy && downloading.format === 'pdf' ? (
                              <Spinner size="sm" />
                            ) : (
                              <FontAwesomeIcon icon={faFilePdf} />
                            )}
                            {' PDF'}
                          </button>
                          <button
                            type="button"
                            className="bz-btn bz-btn--sm"
                            title="Download HTML"
                            disabled={rowBusy}
                            onClick={() => download(job.jobId, 'html')}
                          >
                            {rowBusy && downloading.format === 'html' ? (
                              <Spinner size="sm" />
                            ) : (
                              <FontAwesomeIcon icon={faFileCode} />
                            )}
                            {' HTML'}
                          </button>
                        </div>
                      ) : (
                        <span className="bz-muted">Not ready</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

ExportHistory.propTypes = { projectId: PropTypes.string.isRequired };

export default ExportHistory;
